<template>
	<app-page>
		<app-heading :title="`${survey.name} Results`" />
		<app-row>
			<app-cell>
				<div class="title">Participants</div>
			</app-cell>
		</app-row>
		<app-row>
			<app-cell>
				<app-data-table
					:items="participants"
					:headers="participantsHeaders"
					:total="participants.length"
					:dynamic="false"
				>
					<template v-slot:status="{ item }">
						<div
							:class="{
								[`${participantStatusColors[item.status]}--text`]: true
							}"
						>{{ participantStatuses[item.status] }}</div>
					</template>
					<template v-slot:link="{ item }">
						<a :href="item.link" target="_blank">Click here to access participant survey</a>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>
		<app-row>
			<app-cell>
				<div class="title">Results</div>
			</app-cell>
			<app-cell class="text-right">
				<app-button
					button
					small
					outlined
					color="secondary"
					icon="fa-download"
					@click="exportResults"
				>Export (CSV)</app-button>
			</app-cell>
		</app-row>
		<app-row>
			<app-cell>
				<app-data-table
					:items="results"
					:headers="headers"
					:total="results.length"
					:dynamic="false"
					primary-key="listingId"
					expand
				>
					<template v-slot:expanded-item="{ item }">
						<td :colspan="headers.length + 1" class="pa-2">
							<app-data-table
								:items="detailledResults[item.listindId]"
								:headers="detailledHeaders"
								:total="detailledResults[item.listindId]
									? detailledResults[item.listindId].length : 0"
								:dynamic="false"
							>
								<template v-slot:note="{ item: detailledItem }">
									<app-tooltip v-if="detailledItem.note" :text="detailledItem.note">
										{{ detailledItem.note.substr(0, 30) }}
										<span v-if="detailledItem.note.length > 30">...</span>
									</app-tooltip>
								</template>
								<template v-slot:actions="{ item: detailledItem }">
									<app-button
										v-if="$ravello.can('marketmath.put')"
										color="secondary"
										:to="{ name: 'market-survey-result', params: { result: detailledItem._id } }"
										icon="fa-pencil-alt"
										table
									>
										Edit
									</app-button>
								</template>
							</app-data-table>
						</td>
					</template>
				</app-data-table>
			</app-cell>
		</app-row>
	</app-page>
</template>

<script>
import { exportCollection2 } from '@/utils/import'

const MARKET_MATH_SURVEY_LINK = 'https://www.marketmath.com/participate/'
export default {
	data() {
		return {
			survey: {
				name: '',
				participants: [],
				listings: [],
			},
			results: [],
			participants: [],
			participantStatuses: ['Not started', 'In progress', 'Completed'],
			participantStatusColors: ['warning', 'secondary', 'success'],
			detailledResults: {},
			participantsHeaders: [{
				text: 'Name',
				value: 'name',
				sortable: true,
			}, {
				text: 'Status',
				value: 'status',
				sortable: false,
			}, {
				text: 'Link',
				value: 'link',
				sortable: false,
			}],
			headers: [{
				text: 'Listing',
				value: 'listing',
				sortable: false,
			}, {
				text: 'Score',
				value: 'score',
				sortable: true,
			}, {
				text: 'Revenue',
				value: 'revenue',
				sortable: true,
				format: 'currency',
				formatArgs: ['$', 0],
			}, {
				text: 'Occupancy Rate',
				value: 'occupancy_rate',
				sortable: true,
				cb: item => (item.occupancy_rate ? `${item.occupancy_rate}%` : '0%'),
			}],
			detailledHeaders: [{
				text: 'Participant',
				value: 'participant',
				sortable: false,
			}, {
				text: 'Score',
				value: 'score',
				sortable: true,
			}, {
				text: 'Revenue',
				value: 'revenue',
				sortable: true,
				format: 'currency',
				formatArgs: ['$', 0],
			}, {
				text: 'Occupancy Rate',
				value: 'occupancy_rate',
				sortable: true,
				cb: item => (item.occupancy_rate ? `${item.occupancy_rate}%` : '0%'),
			}, {
				text: 'Available for short term rental?',
				value: 'not_avaible',
				sortable: true,
				cb: item => (item.not_available ? 'No' : 'Yes'),
			}, {
				text: 'Is long term rental?',
				value: 'long_term',
				sortable: true,
				cb: item => (item.long_term ? 'Yes' : 'No'),
			}, {
				text: 'Note',
				value: 'note',
				sortable: false,
			}, {
				text: 'Actions',
				value: 'actions',
				sortable: false,
				align: 'end',
			}],
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
		surveyId() {
			return this.$route.params.survey
		},
	},
	// @todo - Do something smarter, might not be the best way to handle this
	async mounted() {
		this.survey = await this.$ravello.graphql('survey', this.surveyId)
			.parent(this.$ravello.graphql('market', this.marketId))
			.fields([
				'_id',
				'name',
				'participants',
				'completed_participants',
				'listings',
				'access_code',
			])
			.query()

		this.initData()
	},

	methods: {
		async fetchResultsData() {
			return Promise.all([
				this.$ravello.graphql('listings')
					.parent(this.$ravello.graphql('market', this.marketId))
					.filter('_id', this.survey.listings)
					.fields(['_id', 'title', 'mls'])
					.limit(this.survey.listings.length)
					.query(),
				this.$ravello.graphql('participants')
					.parent(this.$ravello.graphql('market', this.marketId))
					.fields(['_id', 'first_name', 'last_name'])
					.filter('_id', this.survey.participants)
					.limit(this.survey.participants.length)
					.query(),
				this.$ravello.graphql('results')
					.parent(this.$ravello.graphql('survey', this.surveyId)
						.parent(this.$ravello.graphql('market', this.marketId)))
					.limit(this.survey.participants.length * this.survey.listings.length)
					.fields(['_id', 'score', 'revenue', 'occupancy_rate',
						'long_term', 'not_available', 'note'])
					.include(this.$ravello.graphql('participant').fields(['_id']))
					.include(this.$ravello.graphql('listing').fields(['_id']))
					.query(),
			])
		},
		calcListingsAverage(listings, results) {
			return listings
				.sort((a, b) => this.survey.listings.indexOf(a._id) - this.survey.listings.indexOf(b._id))
				.map((listing) => {
					const listingName = `${listing.title} (${listing.mls})`
					const listingResults = results.filter(result => result.listing._id === listing._id)
					if (!listingResults.length) {
						return {
							listing: listingName,
							score: 'N/A',
							revenue: 0,
							occupancy_rate: 0,
						}
					}
					const score = listingResults.reduce((acc, result) => acc + result.score, 0)
						/ listingResults.length
					const revenue = listingResults.reduce((acc, result) => acc + result.revenue, 0)
						/ listingResults.length
					const occupancyRate = listingResults
						.reduce((acc, result) => acc + result.occupancy_rate, 0) / listingResults.length

					return {
						listing: listingName,
						listindId: listing._id,
						score: score.toFixed(1),
						revenue,
						occupancy_rate: occupancyRate.toFixed(2),
					}
				})
		},
		getDetailledResults(participants, results) {
			const participantsResults = participants
				.sort((a, b) => this.survey.participants.indexOf(a._id)
					- this.survey.participants.indexOf(b._id))
				.reduce((acc, participant) => {
					const participantName = `${participant.last_name}, ${participant.first_name}`
					return [...acc, ...results
						.filter(result => result.participant._id === participant._id)
						.map(result => ({
							...result,
							participant: participantName,
						}))]
				}, [])

			return this.survey.listings.reduce((acc, id) => ({
				...acc,
				[id]: participantsResults
					.filter(result => result.listing._id === id),
			}), {})
		},
		getParticipantsProgression(participants, results) {
			const completedParticipants = this.survey.completed_participants || []
			const progressingParticipants = results.map(result => result.participant._id)

			return participants.map((participant) => {
				let status = 0
				if (completedParticipants.includes(participant._id)) {
					status = 2
				} else if (progressingParticipants.includes(participant._id)) {
					status = 1
				}

				const link = `${MARKET_MATH_SURVEY_LINK}${this.survey._id}`
					+ `?access_code=${this.survey.access_code}&market=${this.marketId}`
					+ `&participant=${participant._id}`

				return {
					name: `${participant.first_name} ${participant.last_name}`,
					status,
					link,
				}
			})
		},
		async initData() {
			const [listings, participants, results] = await this.fetchResultsData()
			this.results = this.calcListingsAverage(listings, results)
			this.participants = this.getParticipantsProgression(participants, results)
			this.detailledResults = this.getDetailledResults(participants, results)
		},

		createExportHeader(entry) {
			const header = [
				entry.survey.name,
				'State',
				'City',
				'Area',
				'Complex',
				'Bedrooms',
			]

			entry.survey.participants.forEach((participantId) => {
				const { participant } = entry.survey.results.find(result => result.participant._id === participantId)
				if (participant) {
					const name = `${participant.first_name} ${participant.last_name}`
					header.push(`${name} Revenue`)
					header.push('')
					header.push(`${name} ADR`)
					header.push('')
					header.push(`${name} Occupancy`)
					header.push('')
				}
			})
			header.push('Average Revenue')
			header.push('')
			header.push('Average ADR')
			header.push('')
			header.push('Average Occupancy')
			header.push('')
			header.push('Average Score')
			header.push('')
			header.push('Adjusted Average Revenue')
			header.push('Adjusted Average ADR')
			header.push('Adjusted Average Occupancy')
			header.push('Adjusted Average Score')

			return header
		},

		getVariance(src, adjusted) {
			return adjusted ? (src - adjusted) / adjusted : 0
		},

		createExportListingResults(listingId, entry) {
			const results = entry.survey.results.filter(result => result.listing._id === listingId)
			if (!results.length) { return null }
			const [first] = results
			// First row
			const row = [
				first.listing.title,
				entry.name,
				entry.survey.market_city.name,
				first.listing.complex.area.name,
				first.listing.complex.name,
				first.listing.bedrooms,
			]

			// Adjusted averages definition
			const adjustedRevenue = first.listing.revenue
			const adjustedScore = first.listing.score
			const adjustedOccupancyRate = first.listing.occupancy_rate
			const adjustedAdr = adjustedOccupancyRate ? (adjustedRevenue / (365 * (adjustedOccupancyRate / 100))) : null

			// Averages definition
			const averageScore = results.reduce((acc, result) => acc + result.score, 0)
				/ results.length
			const averageRevenue = results.reduce((acc, result) => acc + result.revenue, 0)
				/ results.length
			const averageOccupancyRate = results
				.reduce((acc, result) => acc + result.occupancy_rate, 0) / results.length
			const averageAdr = averageOccupancyRate ? (averageRevenue / (365 * (averageOccupancyRate / 100))) : null

			// Participant data
			entry.survey.participants.forEach((participantId) => {
				const found = results.find(result => result.participant._id === participantId)
				if (found) {
					const participantAdr = found.occupancy_rate ? (found.revenue / (365 * (found.occupancy_rate / 100))) : null
					row.push(found.revenue)
					row.push(this.getVariance(found.revenue, adjustedRevenue))
					if (participantAdr !== null) {
						row.push(participantAdr.toFixed(2))
						row.push(this.getVariance(participantAdr, adjustedAdr))
					} else {
						row.push('N/A')
						row.push('')
					}

					row.push(found.occupancy_rate)
					row.push(this.getVariance(found.occupancy_rate, adjustedOccupancyRate))
				}
			})

			// Averages rows
			row.push(averageRevenue)
			row.push(this.getVariance(averageRevenue, adjustedRevenue))
			if (averageAdr !== null) {
				row.push(averageAdr.toFixed(2))
				row.push(this.getVariance(averageAdr, adjustedAdr))
			} else {
				row.push('N/A')
				row.push('')
			}

			row.push(averageOccupancyRate)
			row.push(this.getVariance(averageOccupancyRate, adjustedOccupancyRate))
			row.push(averageScore)
			row.push(this.getVariance(averageScore, adjustedScore))

			// Adjusted rows
			row.push(adjustedRevenue)
			if (adjustedOccupancyRate !== null) {
				row.push(adjustedAdr.toFixed(2))
			} else {
				row.push('N/A')
			}
			row.push(adjustedOccupancyRate)
			row.push(adjustedScore)

			return row
		},

		async exportResults() {
			const request = this.$ravello.graphql('market', this.marketId)
				.field('name')
				.include(this.$ravello.graphql('survey', this.surveyId)
					.fields(['name', 'listings', 'participants'])
					.include(this.$ravello.graphql('market_city').field('name'))
					.include(this.$ravello.graphql('results')
						.fields(['score', 'revenue', 'occupancy_rate'])
						.limit(this.survey.listings.length * this.survey.participants.length)
						.include(this.$ravello.graphql('participant')
							.fields(['_id', 'first_name', 'last_name']))
						.include(this.$ravello.graphql('listing')
							.fields(['_id', 'title', 'bedrooms', 'score', 'revenue', 'occupancy_rate'])
							.include(this.$ravello.graphql('complex')
								.field('name')
								.include(this.$ravello.graphql('area').field('name'))))))

			await exportCollection2(`survey_${this.survey.name}_results`, 'csv', request, {
				graphql: true,
				limit: false,
				header: false,
				map: entry => [
					this.createExportHeader(entry),
					...entry.survey.listings.map(listingId => this.createExportListingResults(listingId, entry)),
				].filter(row => !!row),
			})
		},
	},
}
</script>
